import {zodResolver} from '@hookform/resolvers/zod';
import {Box, Stack, Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';

import IconEmail from 'presentation/assets/images/change-data-validation/icon-email.svg';
import IconSmartphone from 'presentation/assets/images/change-data-validation/icon-smartphone.svg';
import IconSucess from 'presentation/assets/images/change-data-validation/icon-sucess.svg';
import {AlertInfo, CTAButtonFixed, Modal} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {VALIDATION_CODE_NAME, useVerification} from 'presentation/pages/public';
import {translations} from 'presentation/translations-new';
import {formatMasks} from 'presentation/utils';
import {useChangeEmailMutation} from '../../queries/useChangeEmailMutation';
import {useChangePhoneMutation} from '../../queries/useChangePhoneMutation';
import {AlertEnum} from '../../store/changeDataAlertStore/types';
import {IFormData, formSchema} from './schema';
import {StyledIconImage, StyledInput} from './styles';
import {ConfirmationOtpModalProps, OtpErrorType, SendTokenToUpdateData} from './types';

const {securityCodeModal} = translations['pt-br'].privateFlow.profile.changeData;
const {
	titleEmail,
	titleCellPhone,
	insertCodeEmail,
	insertCodeCellPhone,
	securityCode,
	errorToken,
	resendBtn,
	resendIn,
	resendSuffix,
	loadingText,
	completionTitle,
	cellphoneUpdated,
	emailUpdated,
	close,
} = securityCodeModal;

export const ConfirmationOtpModal = ({
	open,
	setOpen,
	type,
	data,
	handleAbandon,
	onResend,
	loading,
}: ConfirmationOtpModalProps) => {
	const {isMobile} = useMediaQueries();
	const methods = useForm<IFormData>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			code1: '',
			code2: '',
			code3: '',
			code4: '',
			code5: '',
			code6: '',
			code7: '',
		},
	});

	const INITIAL_TIMER = {minutes: 1, seconds: 59};
	const VERIFICATION_CODE_LENGTH = 7;
	const MODAL_MAX_WIDTH = 380;

	const [timer, setTimer] = useState(INITIAL_TIMER);
	const [isSuccess, setIsSuccess] = useState(false);

	const {inputClass, handleChange, handleKeyDown, handlePaste} = useVerification({
		codeLength: VERIFICATION_CODE_LENGTH,
		methods,
	});

	const {register, handleSubmit, reset, watch} = methods;
	const [error, setError] = useState<OtpErrorType>();

	const handleClose = () => {
		setOpen(false);
		setError(undefined);
		setIsSuccess(false);
	};

	const handleSuccess = () => {
		setError(undefined);
		setIsSuccess(true);
	};

	const handleCloseAbandon = () => {
		handleClose();
		handleAbandon();
		setTimer(INITIAL_TIMER);
	};

	const {mutateAsync: updateEmail} = useChangeEmailMutation({
		handleClose,
		handleSuccess,
		setError,
		type,
		reset,
	});
	const {mutateAsync: updatePhone} = useChangePhoneMutation({
		handleClose,
		handleSuccess,
		setError,
		type,
		reset,
	});

	const sendTokenToUpdateData = async ({token, dataType, dataValue}: SendTokenToUpdateData) => {
		if (dataType === AlertEnum.EMAIL) {
			const response = await updateEmail({
				token,
				email: dataValue,
			});
			return response;
		}

		const response = await updatePhone({
			token,
			celular: dataValue,
		});
		return response;
	};

	const onSubmit = async (value: IFormData) => {
		const token =
			value.code1 +
			value.code2 +
			value.code3 +
			value.code4 +
			value.code5 +
			value.code6 +
			value.code7;

		await sendTokenToUpdateData({
			token,
			dataType: type,
			dataValue: data || '',
		});
	};

	const handleChangeSubmit = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		index: number,
	) => {
		if (error) {
			setError(undefined);
		}
		handleChange(e, index);
		if (index === VERIFICATION_CODE_LENGTH - 1) {
			handleSubmit(onSubmit)();
		}
	};

	const handleOnPaste = (e: React.ClipboardEvent<HTMLDivElement>, index: number) => {
		if (error) {
			setError(undefined);
		}
		handlePaste(e, index);
		const {code1, code2, code3, code4, code5, code6, code7} = watch();
		const IS_VALID = !!code1 && !!code2 && !!code3 && !!code4 && !!code5 && !!code6 && !!code7;
		if (IS_VALID) {
			handleSubmit(onSubmit)();
		}
	};

	const handleResend = () => {
		if (onResend) {
			onResend();
		}
		setError(undefined);
		reset();
		setTimer(INITIAL_TIMER);
	};

	const isEmail = type === AlertEnum.EMAIL;
	const formattedData = isEmail ? data : formatMasks('phone', data || '');
	const isTimerRun = timer.minutes >= 0 && timer.seconds >= 0;
	const isTimerComplete = timer.minutes === 0 && timer.seconds === 0;
	const timerLabel = `${String(timer.minutes).padStart(2, '0')}m ${String(timer.seconds).padStart(
		2,
		'0',
	)}s`;

	const handleTimer = () => {
		if (timer.minutes >= 0 && timer.seconds > 0) {
			setTimer(state => ({...state, seconds: state.seconds - 1}));
		}
		if (timer.minutes >= 0 && timer.seconds === 0) {
			setTimer(state => ({minutes: state.minutes - 1, seconds: 59}));
		}
		if (isTimerComplete) {
			setTimer({minutes: 0, seconds: 0});
		}
	};

	useEffect(() => {
		let intervalId: NodeJS.Timeout;
		if (isTimerRun) {
			intervalId = setInterval(() => {
				handleTimer();
			}, 1000);
		}
		return () => clearInterval(intervalId);
	}, [timer]);

	return (
		<Modal.Root
			open={open}
			onClose={isSuccess ? handleClose : handleCloseAbandon}
			customMaxWidth={MODAL_MAX_WIDTH}
			fullScreen={isMobile}
			fullWidth>
			<Modal.Header
				title={isSuccess ? completionTitle : isEmail ? titleEmail : titleCellPhone}
				onClose={isSuccess ? handleClose : handleCloseAbandon}
			/>
			<Modal.Content sx={{px: {xs: 2, sm: 3}, py: 3}}>
				{loading ? <Loading minHeight="280px" text={loadingText} /> : null}

				{!loading && isSuccess ? (
					<Stack spacing={2} alignItems="center" py={3}>
						<StyledIconImage src={IconSucess} alt="" />
						<Typography variant="h6" fontWeight={700} textAlign="center">
							{isEmail ? emailUpdated : cellphoneUpdated}
						</Typography>
						<Typography color="text.secondary" textAlign="center">
							{isEmail ? 'Seu novo e-mail ' : 'Seu novo número '}
							<Box component="span" fontWeight={700}>
								{formattedData}
							</Box>
							{' foi salvo com sucesso. Usaremos ele para enviar avisos do seu plano.'}
						</Typography>
					</Stack>
				) : null}

				{!loading && !isSuccess ? (
					<Stack spacing={3} alignItems="center">
						<StyledIconImage src={isEmail ? IconEmail : IconSmartphone} alt="" />
						<Stack spacing={1} alignItems="center">
							<Typography variant="h6" fontWeight={700} textAlign="center">
								{securityCode}
							</Typography>
							<Typography textAlign="center" color="text.secondary" lineHeight={1.35}>
								{isEmail ? insertCodeEmail : insertCodeCellPhone}
								<Box component="span" display="block" fontWeight={700} mt={0.25}>
									{formattedData}
								</Box>
							</Typography>
						</Stack>
						<Stack direction="row" gap={0.75} justifyContent="center">
							{Array.from({length: VERIFICATION_CODE_LENGTH}, (_, index) => (
								<StyledInput
									{...register(VALIDATION_CODE_NAME[index + 1])}
									type="text"
									key={`code${index + 1}`}
									className={inputClass}
									onPaste={e => handleOnPaste(e, index)}
									onChange={e => handleChangeSubmit(e, index)}
									onKeyDown={e => handleKeyDown(e, index)}
									autoFocus={index === 0}
									autoComplete={index === 0 ? 'one-time-code' : 'off'}
									inputProps={{
										inputMode: 'numeric',
										pattern: '[0-9]*',
										maxLength: 1,
									}}
									error={!!error}
								/>
							))}
						</Stack>
						{error ? (
							<AlertInfo.Root severity="error" sx={{width: '100%'}}>
								{errorToken}
							</AlertInfo.Root>
						) : null}
					</Stack>
				) : null}
			</Modal.Content>

			{!loading ? (
				<Modal.Footer>
					<CTAButtonFixed.Root width={'100%'} sx={{bottom: '10px'}}>
						<CTAButtonFixed.Action
							variant={isSuccess || !isTimerComplete ? 'contained' : 'outlined'}
							onClick={isSuccess ? handleClose : handleResend}
							sx={{height: '42px'}}
							disabled={!isSuccess && !isTimerComplete}
							fullWidth>
							{isSuccess
								? close
								: isTimerComplete
									? resendBtn
									: `${resendIn} ${timerLabel} ${resendSuffix}`}
						</CTAButtonFixed.Action>
					</CTAButtonFixed.Root>
				</Modal.Footer>
			) : null}
		</Modal.Root>
	);
};
